import {
	afterRenderEffect, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, input, output, signal,
	viewChild, viewChildren
} from '@angular/core'
import { TranslocoDirective } from '@jsverse/transloco'
import { BaseIconComponent, CUSTOM_ICONS, IconInput } from '@/shared/components/base/base-icon/base-icon.component'
import { SearchScope } from '@/features/search/search.source'

interface ScopeTab {
	scope: SearchScope
	labelKey: string
	icon: IconInput
}

/**
 * Casino or Sports: which domain the search panel is showing results for.
 *
 * The lit pill is one element behind both tabs, placed on the chosen one from
 * its measured offset and width (the labels differ in length), so a change of
 * scope slides it across rather than swapping two backgrounds.
 */
@Component({
	selector: 'search-scope-tabs',
	templateUrl: './search-scope-tabs.component.html',
	styleUrl: './search-scope-tabs.component.css',
	imports: [TranslocoDirective, BaseIconComponent],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchScopeTabsComponent {
	readonly scope = input.required<SearchScope>()

	readonly scopeChange = output<SearchScope>()

	protected readonly tabs: ScopeTab[] = [
		{ scope: 'casino', labelKey: 'navigation.casino', icon: CUSTOM_ICONS.Casino },
		{ scope: 'sportsbook', labelKey: 'other.sports', icon: CUSTOM_ICONS.FootballBall }
	]

	/** Off until the pill has been placed once, so it appears in place instead of sliding in from the left. */
	protected readonly animated = signal(false)

	private readonly tabList = viewChild<ElementRef<HTMLElement>>('tabList')
	private readonly tabButtons = viewChildren<ElementRef<HTMLElement>>('tabButton')

	constructor() {
		afterRenderEffect(() => this.placeThumb(this.scope()))

		// The labels change width with the language and the font loading in, and
		// the tabs measure 0 while their panel is hidden: place again on resize.
		const observer = new ResizeObserver(() => this.placeThumb(this.scope()))
		afterRenderEffect(() => {
			const tabList = this.tabList()?.nativeElement
			if (tabList) observer.observe(tabList)
		})
		inject(DestroyRef).onDestroy(() => observer.disconnect())
	}

	protected select(scope: SearchScope): void {
		if (scope !== this.scope()) this.scopeChange.emit(scope)
	}

	private placeThumb(scope: SearchScope): void {
		const tabList = this.tabList()?.nativeElement
		const button = this.tabButtons()[this.tabs.findIndex((tab) => tab.scope === scope)]?.nativeElement
		if (!tabList || !button) return
		// Hidden (its results slot is not showing): place it without a slide once it shows.
		if (button.offsetWidth === 0) {
			this.animated.set(false)
			return
		}

		tabList.style.setProperty('--thumb-x', `${button.offsetLeft}px`)
		tabList.style.setProperty('--thumb-width', `${button.offsetWidth}px`)
		// Next frame, so this placement is not itself animated.
		if (!this.animated()) requestAnimationFrame(() => this.animated.set(true))
	}
}
